import type {
	AdminPaymentDispute,
	AdminPaymentRecord,
	DisputeResolutionLegState,
	DisputeResolutionOutcome,
} from "@shared/types";
import { apiClient } from "@/lib/api-client";

export { DISPUTE_RESOLUTION_OUTCOME_PERMISSIONS } from "@shared/types";
export type {
	AdminPaymentDispute,
	AdminPaymentRecord,
	DisputeResolutionLegState,
	DisputeResolutionOutcome,
};

/**
 * What `POST /admin/disputes/{id}/resolve` answers: the dispute as it stands
 * once the resolution's legs have run, resolved or still `resolving`, and its
 * escrow row. A leg the backend refuses fails the request instead.
 */
export interface DisputeResolutionResult {
	dispute: AdminPaymentDispute;
	transaction: AdminPaymentRecord["transaction"];
}

export async function listAdminPayments(
	filters: Record<string, string | number | undefined>,
) {
	const response = await apiClient.get("/api/v1/admin/payments", {
		params: filters,
	});
	return response.data.data.payments as AdminPaymentRecord[];
}

export async function getAdminPayment(transactionId: string) {
	const response = await apiClient.get(
		`/api/v1/admin/payments/${transactionId}`,
	);
	return response.data.data.payment as AdminPaymentRecord;
}

export async function releaseAdminPayment(
	transactionId: string,
	reason: string,
) {
	const response = await apiClient.post(
		`/api/v1/admin/payments/${transactionId}/release`,
		{ reason },
	);
	return response.data.data;
}

/**
 * Registers a payout account the stub payment provider registered with the
 * active provider. The backend refuses (409) any other account.
 */
export async function resyncPayoutAccount(
	accountId: string,
): Promise<{ payoutAccountId: string; providerAccountId: string }> {
	const response = await apiClient.post(
		`/api/v1/admin/payments/payout-accounts/${accountId}/resync`,
	);
	return response.data.data as {
		payoutAccountId: string;
		providerAccountId: string;
	};
}

export async function createPaymentDispute(
	transactionId: string,
	input: {
		reason: AdminPaymentDispute["reason"];
		details?: string;
		evidence?: string[];
	},
) {
	const response = await apiClient.post(
		`/api/v1/admin/payments/${transactionId}/disputes`,
		input,
	);
	return response.data.data.dispute as AdminPaymentDispute;
}

export async function resolvePaymentDispute(
	disputeId: string,
	input: {
		outcome: DisputeResolutionOutcome;
		reason: string;
		refundAmount?: number;
	},
): Promise<DisputeResolutionResult> {
	const response = await apiClient.post(
		`/api/v1/admin/disputes/${disputeId}/resolve`,
		input,
	);
	return response.data.data as DisputeResolutionResult;
}

/** Cancels a resolution no leg of which was sent; the dispute returns to open. */
export async function cancelPaymentDisputeResolution(
	disputeId: string,
	reason: string,
): Promise<{ dispute: AdminPaymentDispute }> {
	const response = await apiClient.post(
		`/api/v1/admin/disputes/${disputeId}/resolution/cancel`,
		{ reason },
	);
	return response.data.data as { dispute: AdminPaymentDispute };
}

export interface ReconcileRefundsResult {
	/** Candidates looked at: resolutions whose refund leg was pending. */
	scanned: number;
	/** Resolutions finished as resolved_refund or resolved_partial. */
	resolved: number;
	/** Refund legs the provider reports failed, now recorded failed. */
	failed: number;
	/**
	 * Refund legs the provider reports processed for another amount, or
	 * without one, now recorded failed for manual review rather than finished.
	 */
	flagged: number;
	hasMore: boolean;
}

export async function getRefundReconcileStatus(): Promise<{
	pending: number;
	/** Refund legs the sweep recorded failed, including amount mismatches. */
	failed: number;
}> {
	const response = await apiClient.get(
		"/api/v1/admin/payments/refunds/reconcile-status",
	);
	return response.data.data as { pending: number; failed: number };
}

export async function reconcileRefunds(
	limit?: number,
): Promise<ReconcileRefundsResult> {
	const response = await apiClient.post(
		"/api/v1/admin/payments/refunds/reconcile",
		limit !== undefined ? { limit } : {},
	);
	return response.data.data as ReconcileRefundsResult;
}

export interface ReconcilePayoutsResult {
	/** Held rows whose transfer was looked up. */
	scanned: number;
	/** Rows recorded released from a transfer the provider reports paid. */
	released: number;
	/** Recent releases re-checked for a reversal (a release or a split's payout). */
	reversalChecked: number;
	/** Recent releases the provider now reports failed or reversed, surfaced for support. */
	reversed: number;
	/** Rows whose transfer the provider reports failed or rejected. */
	failed: number;
	/** Rows the provider paid for another amount than the row holds. */
	flagged: number;
	hasMore: boolean;
}

export async function reconcilePayouts(
	limit?: number,
): Promise<ReconcilePayoutsResult> {
	const response = await apiClient.post(
		"/api/v1/admin/payments/payouts/reconcile",
		limit !== undefined ? { limit } : {},
	);
	return response.data.data as ReconcilePayoutsResult;
}
